import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Check, Dices, GripVertical, RefreshCw, Users } from 'lucide-react';
import { useParticipants } from '@/data/use-participants';
import { applySeeding } from '@/data/seed-participants';
import { EmptyState, LoadingState, ErrorState } from '@/components/design';
import { toast } from '@/components/ui/sonner';
import { SortableParticipantRow } from './sortable-participant-row';
import type { SeedingMode, Tournament, Participant } from '@/data/types';

interface Props {
  tournament: Tournament;
}

export function SeedingTab({ tournament }: Props) {
  const { participants, error } = useParticipants(tournament.id);
  const [mode, setMode] = useState<SeedingMode>(tournament.seedingMode);
  const [order, setOrder] = useState<Participant[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!participants) return;
    // Начальная сортировка нового состава: по seed (если есть), затем по имени.
    const initialSort = (list: Participant[]): Participant[] =>
      [...list].sort((a, b) => {
        const sa = a.seed ?? Number.POSITIVE_INFINITY;
        const sb = b.seed ?? Number.POSITIVE_INFINITY;
        if (sa !== sb) return sa - sb;
        return a.displayName.localeCompare(b.displayName, 'ru');
      });

    // Не сбрасываем ручной drag&drop-порядок при фоновых snapshot'ах Firestore.
    // Если состав участников тот же — только освежаем данные, сохраняя порядок.
    setOrder((prev) => {
      const byId = new Map(participants.map((p) => [p.id, p]));
      const sameSet =
        prev.length === participants.length && prev.every((p) => byId.has(p.id));
      if (sameSet) {
        return prev.map((p) => byId.get(p.id)!);
      }
      // Состав изменился: сохраняем порядок оставшихся, новых добавляем в конец.
      const prevIds = new Set(prev.map((p) => p.id));
      const kept = prev.filter((p) => byId.has(p.id)).map((p) => byId.get(p.id)!);
      const added = initialSort(participants.filter((p) => !prevIds.has(p.id)));
      return prev.length === 0 ? initialSort(participants) : [...kept, ...added];
    });
  }, [participants]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrder((items) => {
      const oldIdx = items.findIndex((p) => p.id === active.id);
      const newIdx = items.findIndex((p) => p.id === over.id);
      return arrayMove(items, oldIdx, newIdx);
    });
  }

  function handleShuffle() {
    setOrder((items) => {
      const shuffled = [...items];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    });
  }

  async function handleSave() {
    if (order.length < 2) {
      toast.error('Нужно минимум 2 участника');
      return;
    }
    setSubmitting(true);
    try {
      const updates =
        mode === 'MANUAL'
          ? order.map((p, idx) => ({ participantId: p.id, seed: idx + 1 }))
          : order.map((p) => ({ participantId: p.id, seed: null }));
      await applySeeding(tournament.id, mode, updates, 'READY');
      toast.success('Жеребьёвка зафиксирована');
    } catch (err) {
      toast.error('Не получилось', { description: String((err as Error).message) });
    } finally {
      setSubmitting(false);
    }
  }

  if (error) return <ErrorState body={error.message} />;
  if (participants === undefined) return <LoadingState label="Загрузка..." />;
  if (participants.length < 2) {
    return (
      <EmptyState
        icon={Users}
        title="Недостаточно участников"
        body="Добавьте минимум 2 участников во вкладке «Участники», прежде чем расставлять."
      />
    );
  }

  const locked = tournament.status !== 'DRAFT' && tournament.status !== 'READY';

  const modeHint =
    mode === 'MANUAL'
      ? 'Перетащите участников: сильнейшие — наверху.'
      : 'Порядок генерируется при старте, список ниже — справочный.';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 360px) 1fr',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      {/* Левая колонка */}
      <div className="card-d card-pad">
        <div className="t-eyebrow" style={{ marginBottom: 12 }}>
          Режим жеребьёвки
        </div>

        <div style={{ display: 'grid', gap: 8 }}>
          <button
            type="button"
            className="fmt-card"
            aria-selected={mode === 'RANDOM'}
            onClick={() => !locked && setMode('RANDOM')}
            disabled={locked}
          >
            <div className="fmt-name">
              <Dices size={16} /> Случайная
            </div>
            <div className="fmt-desc">
              Порядок сгенерируется при старте турнира. Список справа — справочный.
            </div>
          </button>

          <button
            type="button"
            className="fmt-card"
            aria-selected={mode === 'MANUAL'}
            onClick={() => !locked && setMode('MANUAL')}
            disabled={locked}
          >
            <div className="fmt-name">
              <GripVertical size={16} /> Ручная расстановка
            </div>
            <div className="fmt-desc">
              Перетащите участников в порядке силы. Сильнейшие — наверху.
            </div>
          </button>
        </div>

        {mode === 'RANDOM' && !locked && (
          <button
            className="btn"
            onClick={handleShuffle}
            style={{ width: '100%', marginTop: 12 }}
          >
            <RefreshCw size={14} /> Перетасовать
          </button>
        )}

        {!locked && (
          <>
            <hr className="divider" />
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={submitting}
              style={{ width: '100%' }}
            >
              {submitting ? <span className="spinner" /> : <Check size={14} />}
              {submitting ? 'Сохраняем...' : 'Зафиксировать жеребьёвку'}
            </button>
            <p className="help" style={{ marginTop: 8 }}>
              После фиксации сетка будет сгенерирована.
            </p>
          </>
        )}

        {locked && (
          <p className="help" style={{ marginTop: 12 }}>
            Турнир запущен — жеребьёвка уже не редактируется.
          </p>
        )}
      </div>

      {/* Правая колонка */}
      <div className="card-d" style={{ overflow: 'hidden' }}>
        <div
          className="row"
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div className="td-h3">Порядок посева</div>
            <div className="t-xs t-mute">{modeHint}</div>
          </div>
          <span className="chip">{order.length} участников</span>
        </div>

        <div style={{ padding: 14 }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order.map((p) => p.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'grid', gap: 8 }}>
                {order.map((p, idx) => (
                  <SortableParticipantRow key={p.id} participant={p} index={idx} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </div>
  );
}
